import { Component } from '@theme/component';

/**
 * Publica un evento personalizado para el píxel de analítica (GA4) sin romper la página si no existe.
 * @param {string} nombre
 * @param {Record<string, unknown>} [datos]
 */
function publicarEvento(nombre, datos = {}) {
  try {
    window.Shopify?.analytics?.publish?.(nombre, datos);
  } catch {
    // La analítica nunca debe interrumpir la calculadora.
  }
}

const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 });

/**
 * @typedef {object} Refs
 * @property {HTMLFormElement} formulario
 * @property {HTMLInputElement} margen
 * @property {HTMLInputElement} descuento
 * @property {HTMLInputElement} [gastos]
 * @property {HTMLElement} resultados
 * @property {HTMLElement} unidades
 * @property {HTMLElement} detalle
 * @property {HTMLElement} alerta
 * @property {HTMLElement} [equilibrioHoy]
 * @property {HTMLElement} [equilibrioDescuento]
 * @property {HTMLAnchorElement} [revisar] - Botón bajo el resultado; lleva el cálculo a la agenda (?calculo=).
 */

/**
 * Calculadora de margen: cuánto más hay que vender para compensar un descuento y, en modo completo,
 * el punto de equilibrio mensual con y sin descuento. Todo se calcula en el navegador.
 *
 * Con margen bruto m y descuento d (en % del precio original), cada unidad deja m − d; para ganar lo
 * mismo hacen falta m / (m − d) unidades por cada una de antes, y la facturación cambia en la misma
 * proporción multiplicada por el nuevo precio (1 − d). El punto de equilibrio es gastos ÷ margen sobre
 * la venta: m antes y (m − d) / (1 − d) con el descuento.
 *
 * @extends {Component<Refs>}
 */
class OjCalculadoraComponent extends Component {
  requiredRefs = ['formulario', 'margen', 'descuento', 'resultados', 'unidades', 'detalle', 'alerta'];

  #usada = false;

  connectedCallback() {
    super.connectedCallback();
    // El primer resultado ya viene de Liquid; esto solo prepara el enlace y el resumen para el correo.
    this.calcular();
  }

  /**
   * Recalcula con los valores actuales de los campos.
   * @param {Event} [event]
   */
  calcular(event) {
    if (event?.type === 'submit') event.preventDefault();

    const { margen, descuento, gastos, resultados, unidades, detalle, alerta, equilibrioHoy, equilibrioDescuento } =
      this.refs;
    const m = this.#numero(margen, 0, 95);
    const d = this.#numero(descuento, 0, 95);
    const margenNuevo = m - d;
    const sinMargen = m <= 0 || margenNuevo <= 0;

    resultados.toggleAttribute('data-sin-margen', sinMargen);
    alerta.hidden = !sinMargen;

    let resumen = `Margen bruto ${decimal.format(m)}%, descuento ${decimal.format(d)}%: `;

    if (sinMargen) {
      resumen += 'el descuento supera el margen.';
      unidades.textContent = '—';
      detalle.textContent = '';
      if (equilibrioDescuento) equilibrioDescuento.textContent = '—';
      if (equilibrioHoy) equilibrioHoy.textContent = m > 0 && gastos ? pesos.format(this.#gastos() * (100 / m)) : '—';
    } else {
      const aumentoUnidades = (m / margenNuevo - 1) * 100;
      const aumentoVentas = ((m / margenNuevo) * ((100 - d) / 100) - 1) * 100;
      const margenSobrePrecio = (margenNuevo / (100 - d)) * 100;

      unidades.textContent = `+${Math.round(aumentoUnidades)}`;
      detalle.textContent = `Es decir, ${Math.round(aumentoVentas)}% más de facturación. Tu margen baja de ${decimal.format(
        m
      )}% a ${decimal.format(margenSobrePrecio)}% de cada venta.`;

      resumen += `para ganar lo mismo hay que vender ${Math.round(aumentoUnidades)}% más unidades (${Math.round(
        aumentoVentas
      )}% más de facturación); el margen baja a ${decimal.format(margenSobrePrecio)}% de cada venta.`;

      if (gastos && equilibrioHoy && equilibrioDescuento) {
        const fijos = this.#gastos();
        equilibrioHoy.textContent = pesos.format(fijos * (100 / m));
        equilibrioDescuento.textContent = pesos.format((fijos * (100 - d)) / margenNuevo);
        resumen += ` Gastos fijos de ${pesos.format(fijos)} al mes: punto de equilibrio de ${equilibrioHoy.textContent} sin descuento y ${equilibrioDescuento.textContent} con descuento.`;
      }
    }

    this.#actualizarSalida(resumen);

    if (event && !this.#usada) {
      this.#usada = true;
      publicarEvento('calculadora_margen', { modo: this.dataset.modo || 'descuento' });
    }
  }

  /**
   * Lleva el cálculo al botón de agenda (?calculo=) y al formulario «Envíame este cálculo».
   * @param {string} resumen
   */
  #actualizarSalida(resumen) {
    const { revisar } = this.refs;
    if (revisar?.dataset.destino) {
      const destino = new URL(revisar.dataset.destino, window.location.origin);
      destino.searchParams.set('calculo', resumen);
      revisar.href = destino.origin === window.location.origin ? `${destino.pathname}${destino.search}` : destino.href;
    }
    const campo = this.querySelector('[data-oj-envio-resumen]');
    if (campo instanceof HTMLInputElement) campo.value = resumen;
  }

  /** @returns {number} */
  #gastos() {
    const { gastos } = this.refs;
    if (!gastos) return 0;
    return Math.max(this.#numero(gastos, 0, Number.MAX_SAFE_INTEGER), 0);
  }

  /**
   * Lee un campo numérico (acepta coma decimal) y lo limita al rango.
   * @param {HTMLInputElement} campo
   * @param {number} minimo
   * @param {number} maximo
   */
  #numero(campo, minimo, maximo) {
    const valor = Number.parseFloat(String(campo.value).replace(',', '.'));
    if (!Number.isFinite(valor)) return minimo;
    return Math.min(Math.max(valor, minimo), maximo);
  }
}

if (!customElements.get('oj-calculadora-component')) {
  customElements.define('oj-calculadora-component', OjCalculadoraComponent);
}
